import {BehaviorSubject} from "rxjs/BehaviorSubject";

export class ConditionalBehaviorSubject<T> extends BehaviorSubject<T> {
  private readonly doUpdate: (current: T, next: T) => boolean;

  /**
   * Evaluator function that will always update a value in a {ConditionalBehaviorSubject} (default).
   * @param {T} current
   * @param {T} next
   * @returns {boolean}
   */
  static defaultCondition = <T>(current: T, next: T) => true; // eslint-disable-line

  /**
   * Evaluator function that will only update a value in a {ConditionalBehaviorSubject} if the new value does not match
   * the value currently stored in the {ConditionalBehaviorSubject}.
   * @param {T} current
   * @param {T} next
   * @returns {boolean}
   */
  static onlyUpdateIfDifferent = <T>(current: T, next: T) => current !== next;

  /**
   * Creates a new {ConditionalBehaviorSubject}
   * @param {T} value to start with
   * @param {(current: T, next: T) => boolean} doUpdate check to see if value should be updated
   *   (default is ConditionalBehaviorSubject.defaultCondition)
   */
  constructor(value: T, doUpdate?: (current: T, next: T) => boolean) {
    super(value);

    this.doUpdate = doUpdate || ConditionalBehaviorSubject.defaultCondition;
  }

  /**
   * Send a new value to this {ConditionalBehaviorSubject}
   * @param {T} value to send
   * @returns {boolean} whether the value was accepted/updated
   */
  next(value: T): boolean {
    if (this.doUpdate(this.getValue(), value)) {
      super.next(value);
      return true;
    }

    return false;
  }
}
